import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useRedirect from 'src/hooks/useRedirect';
import EditSupplierSidePanelContainer from 'src/sections/manage_suppliers/EditSupplierSidePanelContainer';
import useGetProgramInfo from 'src/services/marketplace/useGetProgramInfo';
import { useContextSelector } from 'use-context-selector';
import { ShoppingCartContext } from './ShoppingCartContext';

const ShoppingCartSupplierInfoSidePanelContainer: FC = () => {
  const { t } = useTranslation();
  const { redirect } = useRedirect();

  const selectedSupplierForInfo = useContextSelector(
    ShoppingCartContext,
    (context) => context.selectedSupplierForInfo
  );
  const closeSupplierInfo = useContextSelector(
    ShoppingCartContext,
    (context) => context.closeSupplierInfo
  );
  const supplierForInfo = useContextSelector(
    ShoppingCartContext,
    (context) => context.supplierForInfo
  );
  const isLoadingSupplierInfo = useContextSelector(
    ShoppingCartContext,
    (context) => context.isLoadingSupplierInfo
  );
  const isErrorSupplierInfo = useContextSelector(
    ShoppingCartContext,
    (context) => context.isErrorSupplierInfo
  );

  const [selectedProgramInfoId, setSelectedProgramInfoId] = useState<string | null>(null);

  const {
    data: programInfo,
    isLoading: isLoadingProgramInfo,
    isError: isErrorProgramInfo,
    isFetched: isProgramInfoFetched,
  } = useGetProgramInfo(selectedProgramInfoId);

  useEffect(() => {
    if (!selectedSupplierForInfo || isLoadingSupplierInfo || !isErrorSupplierInfo) {
      return;
    }

    toast.error(t('Failed to load data'));
    closeSupplierInfo();
  }, [
    closeSupplierInfo,
    isErrorSupplierInfo,
    isLoadingSupplierInfo,
    selectedSupplierForInfo,
    t,
  ]);

  useEffect(() => {
    if (selectedProgramInfoId === null || isLoadingProgramInfo) {
      return;
    }

    if (isErrorProgramInfo) {
      toast.error(
        t('Unable to open program information. Please connect with the administrator.')
      );
      setSelectedProgramInfoId(null);
      return;
    }

    if (!isProgramInfoFetched || !programInfo) {
      return;
    }

    if (programInfo.myAvendraLink) {
      redirect(programInfo.myAvendraLink, true, true);
    } else {
      toast.error(
        t('Unable to open program information. Please connect with the administrator.')
      );
    }

    setSelectedProgramInfoId(null);
  }, [
    isErrorProgramInfo,
    isLoadingProgramInfo,
    isProgramInfoFetched,
    programInfo,
    redirect,
    selectedProgramInfoId,
    t,
  ]);

  const isPanelOpen = selectedSupplierForInfo !== null;

  if (!selectedSupplierForInfo) {
    return null;
  }

  return (
    <EditSupplierSidePanelContainer
      supplier={supplierForInfo}
      isOpen={isPanelOpen}
      onClose={closeSupplierInfo}
      onProgramInfoClick={setSelectedProgramInfoId}
      viewUntilEdit
    />
  );
};

export default ShoppingCartSupplierInfoSidePanelContainer;
